import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
import ReactECharts, { type EChartsOption } from 'echarts-for-react';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, type SwiperProps, type SwiperRef, SwiperSlide } from 'swiper/react';
import { chunkArray, getCorrectMetricValuesOverViewChart, getPercentDisplay, sortDoughnutSeriesByValue } from '@/modules/finances/lib/doughnut-chart-utils';
import type { AnalyticMetric, DoughnutBudgetMetric, DoughnutSeries } from '@/modules/finances/types';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { usLocalizedNumber } from '@/modules/shared/lib/humanization';
import { CardLegend } from './card-legend';
import { DoughnutChartSkeleton } from './doughnut-chart-skeleton';
import { UsdsTooltip } from './usds-tooltip';
interface DoughnutChartProps {
  seriesData: DoughnutSeries[];
  selectedMetric: AnalyticMetric;
  changeAlignment: boolean;
  showSwiper: boolean;
  numberSliderPerLevel?: number;
}
function DoughnutChart(t0) {
  const $ = _c(59);
  const {
    seriesData,
    selectedMetric,
    changeAlignment,
    showSwiper,
    numberSliderPerLevel: t1
  } = t0;
  const numberSliderPerLevel = t1 === undefined ? 6 : t1;
  const chartRef = useRef(null);
  const ref = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleSeries, setVisibleSeries] = useState(seriesData);
  const [legends, setLegends] = useState(seriesData);
  const isDeepLevel = seriesData.length > 6;
  let t2;
  let t3;
  if ($[0] !== seriesData) {
    t2 = () => {
      startTransition(() => {
        setVisibleSeries(seriesData);
        setLegends(seriesData);
        chartRef.current?.getEchartsInstance()?.resize();
      });
    };
    t3 = [seriesData];
    $[0] = seriesData;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      from: "sm",
      to: "lg"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isTable = useMediaQuery(t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      from: "lg",
      to: "xl"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const isDesktop1024 = useMediaQuery(t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const onResize = () => {
        const chartInstance = chartRef.current?.getEchartsInstance();
        chartInstance?.resize();
      };
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    };
    t7 = [];
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  bb0: switch (selectedMetric) {
    case "PaymentsOnChain":
      {
        t8 = "Net Expenses On-Chain";
        break bb0;
      }
    case "ProtocolNetOutflow":
      {
        t8 = "Net Protocol Outflow";
        break bb0;
      }
    case "Budget":
      {
        t8 = "Budget";
        break bb0;
      }
    case "Forecast":
      {
        t8 = "Forecast";
        break bb0;
      }
    case "Actuals":
      {
        t8 = "Actuals";
        break bb0;
      }
    default:
      {
        t8 = "Budget";
      }
  }
  const tooltipSelectedMetricLabel = t8;
  const doughnutSeriesChunks = chunkArray(sortDoughnutSeriesByValue(seriesData), numberSliderPerLevel);
  let t9;
  if ($[7] !== visibleSeries) {
    t9 = visibleSeries.map(_temp);
    $[7] = visibleSeries;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      show: false,
      position: "center"
    };
    t11 = {
      width: 40
    };
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  let t12;
  if ($[11] !== selectedMetric || $[12] !== tooltipSelectedMetricLabel || $[13] !== visibleSeries) {
    t12 = {
      extraCssText: "z-index:6;border-radius: 12px;overflow: hidden;box-shadow: var(--shadow-md);",
      show: true,
      trigger: "item",
      label: t10,
      emphasis: t11,
      padding: 0,
      borderWidth: 1,
      formatter(params) {
        const index = visibleSeries.findIndex(data_0 => data_0.name === params.name);
        const itemRender = visibleSeries[index];
        const selectedMetricKey = getCorrectMetricValuesOverViewChart(selectedMetric) as keyof DoughnutBudgetMetric;
        const customTooltip = `
          <div style="background-color:var(--popover);padding:7px 15px;min-width:194px;">
            <div style="color:var(--foreground);font-size: 18px;font-weight: 700;line-height: 120%;">${getPercentDisplay(itemRender.percent)}%</div>
            <div style="margin-bottom:8px;color:var(--muted-foreground);font-size: 14px;font-weight: 600;line-height: 22px;max-width: 300px; white-space: nowrap;overflow: hidden; text-overflow: ellipsis;">${itemRender.name}</div>
            <div style="display:flex;flex-direction:row;gap:32px">
                <div style="display:flex;flex-direction:column">
                  <div style="margin-bottom:4;color:var(--foreground);font-size: 16px;font-weight: 600;line-height: 24px;">${usLocalizedNumber(itemRender.metrics[selectedMetricKey === "budget" ? "paymentsOnChain" : selectedMetricKey].value, 2)}</div>
                  <div style="color:var(--muted-foreground);font-size: 12px;font-weight: 500;line-height: 18px;">${selectedMetric === "Budget" ? "Net Expenses On-Chain" : tooltipSelectedMetricLabel}</div>
               </div>
                <div style="display:flex;flex-direction:column">
                  <div style="margin-bottom:4;color:var(--foreground);font-size: 16px;font-weight: 600;line-height: 24px;">${usLocalizedNumber(itemRender.metrics.budget.value, 2)}</div>
                  <div style="color:var(--muted-foreground);font-size: 12px;font-weight: 500;line-height: 18px;">Budget Cap</div>
               </div>
            </div>
          </div>
          `;
        return customTooltip;
      }
    };
    $[11] = selectedMetric;
    $[12] = tooltipSelectedMetricLabel;
    $[13] = visibleSeries;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== isDesktop1024 || $[16] !== isTable) {
    t13 = isTable || isDesktop1024 ? [32, 64] : [35, 70];
    $[15] = isDesktop1024;
    $[16] = isTable;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  let t15;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = ["50%", "50%"];
    t15 = {
      normal: {
        show: false
      }
    };
    t16 = {
      normal: {
        show: false
      }
    };
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
  } else {
    t14 = $[18];
    t15 = $[19];
    t16 = $[20];
  }
  let t17;
  if ($[21] !== visibleSeries) {
    t17 = visibleSeries.map(_temp2);
    $[21] = visibleSeries;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t13 || $[24] !== t17) {
    t18 = [{
      name: "Overview Card",
      type: "pie",
      radius: t13,
      center: t14,
      label: t15,
      labelLine: t16,
      data: t17
    }];
    $[23] = t13;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t12 || $[27] !== t18 || $[28] !== t9) {
    t19 = {
      color: t9,
      tooltip: t12,
      series: t18
    };
    $[26] = t12;
    $[27] = t18;
    $[28] = t9;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const options = t19;
  let t20;
  if ($[30] !== legends || $[31] !== visibleSeries) {
    t20 = seriesName => {
      const chartInstance_0 = chartRef.current?.getEchartsInstance();
      const newArray = visibleSeries.map(item => {
        if (item.name === seriesName) {
          chartInstance_0.dispatchAction({
            type: "hideTip"
          });
          const isVisible = !item.isVisible;
          return {
            ...item,
            isVisible,
            value: isVisible ? item.originalValue || 0 : -1
          };
        }
        return item;
      });
      const newLegend = legends.map(item_0 => {
        if (item_0.name === seriesName) {
          chartInstance_0.dispatchAction({
            type: "hideTip"
          });
          const isVisible_0 = !item_0.isVisible;
          return {
            ...item_0,
            color: isVisible_0 ? item_0.originalColor : "rgb(204, 204, 204)",
            isVisible: isVisible_0,
            value: item_0.originalValue ?? 0
          };
        }
        return item_0;
      });
      setLegends(newLegend as DoughnutSeries[]);
      setVisibleSeries(newArray);
    };
    $[30] = legends;
    $[31] = visibleSeries;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const toggleSeriesVisibility = t20;
  let t21;
  if ($[33] !== visibleSeries) {
    t21 = legendName => {
      const dataIndex = visibleSeries.find(data_2 => data_2.name === legendName);
      if (dataIndex) {
        if (dataIndex.value !== 0) {
          const chartInstance_1 = chartRef.current.getEchartsInstance();
          chartInstance_1.dispatchAction({
            type: "highlight",
            name: legendName,
            seriesIndex: 0
          });
          chartInstance_1.dispatchAction({
            type: "showTip",
            name: legendName,
            seriesIndex: 0
          });
        }
      }
    };
    $[33] = visibleSeries;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  const onLegendItemHover = t21;
  let t22;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = legendName_0 => {
      const chartInstance_2 = chartRef.current.getEchartsInstance();
      chartInstance_2.dispatchAction({
        type: "downplay",
        name: legendName_0
      });
      chartInstance_2.dispatchAction({
        type: "hideTip"
      });
    };
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const onLegendItemLeave = t22;
  let t23;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      pagination: {
        type: "bullets",
        enabled: true,
        clickable: true
      },
      breakpoints: {
        768: {
          spaceBetween: 16
        },
        1024: {
          spaceBetween: 2
        },
        1280: {
          spaceBetween: 2
        },
        1440: {
          spaceBetween: 2
        },
        1920: {
          spaceBetween: 2
        }
      }
    };
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  const swiperOptions = t23;
  let t24;
  let t25;
  if ($[37] !== options) {
    t24 = () => {
      chartRef.current?.getEchartsInstance()?.setOption(options, {
        notMerge: true,
        replaceAnimation: true
      });
    };
    t25 = [options];
    $[37] = options;
    $[38] = t24;
    $[39] = t25;
  } else {
    t24 = $[38];
    t25 = $[39];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      startTransition(() => {
        setIsLoading(false);
      });
    };
    t27 = [];
    $[40] = t26;
    $[41] = t27;
  } else {
    t26 = $[40];
    t27 = $[41];
  }
  useEffect(t26, t27);
  if (isLoading) {
    let t28;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <div className="flex w-full justify-center"><DoughnutChartSkeleton /></div>;
      $[42] = t28;
    } else {
      t28 = $[42];
    }
    return t28;
  }
  const haveMoreThanThreeItems = seriesData.length > 3;
  const t28 = isDeepLevel ? "xl:gap-10" : "xl:gap-8";
  const t29 = haveMoreThanThreeItems && "xl:ml-4 xl:justify-start";
  const t30 = haveMoreThanThreeItems && "2xl:ml-0";
  let t31;
  if ($[43] !== t28 || $[44] !== t29 || $[45] !== t30) {
    t31 = cn("flex w-full", "md:flex-row md:justify-center md:gap-5", "lg:gap-7.5", t28, t29, t30);
    $[43] = t28;
    $[44] = t29;
    $[45] = t30;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = cn("relative flex justify-start", "md:w-34.5 md:min-w-34.5", "xl:w-39.5 xl:min-w-39.5");
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  let t34;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      height: "100%",
      width: "100%"
    };
    t34 = {
      renderer: "svg"
    };
    $[48] = t33;
    $[49] = t34;
  } else {
    t33 = $[48];
    t34 = $[49];
  }
  let t35;
  if ($[50] !== options) {
    t35 = <ReactECharts ref={chartRef} className="chart-container" option={options} style={t33} opts={t34} />;
    $[50] = options;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="absolute inset-0 m-auto flex h-16 w-16 flex-row items-center justify-center"><UsdsTooltip /></div>;
    $[52] = t36;
  } else {
    t36 = $[52];
  }
  let t37;
  if ($[53] !== t35) {
    t37 = <div className={t32}>{t35}{t36}</div>;
    $[53] = t35;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  const t38 = showSwiper ? <div className={cn("md:relative md:flex md:w-50", "lg:flex lg:w-62.5 lg:min-w-62.5", isDeepLevel ? "lg:mt-0" : "lg:mt-4", "xl:relative xl:flex", numberSliderPerLevel === 12 && "xl:min-w-75", numberSliderPerLevel === 12 && "2xl:min-w-102.5", "[&_.swiper-pagination-horizontal]:flex [&_.swiper-pagination-horizontal]:justify-center", "[&_.swiper-pagination-horizontal]:bottom-0! [&_.swiper-pagination-horizontal]:-translate-x-6!", "[&_.swiper-pagination-horizontal]:z-8", "[&_.swiper-pagination-bullet]:bg-muted! [&_.swiper-pagination-bullet]:h-2! [&_.swiper-pagination-bullet]:w-2! [&_.swiper-pagination-bullet]:rounded-full", "[&_.swiper-pagination-bullet-active]:bg-foreground!", "[&_.swiper-pagination-bullet:first-of-type]:rounded-l-md", "[&_.swiper-pagination-bullet:last-of-type]:rounded-r-md")}><Swiper direction="horizontal" ref={ref} modules={[Pagination, Navigation]} centerInsufficientSlides={true} pagination={true} {...swiperOptions} className="h-full w-full">{Array.from(doughnutSeriesChunks.entries()).map(t39 => {
        const [index_0, dataChunk] = t39;
        return <SwiperSlide key={index_0}><CardLegend changeAlignment={changeAlignment} doughnutSeriesData={dataChunk} toggleSeriesVisibility={toggleSeriesVisibility} onLegendItemHover={onLegendItemHover} onLegendItemLeave={onLegendItemLeave} isDeepLevel={isDeepLevel} /></SwiperSlide>;
      })}</Swiper></div> : <div className={cn("relative flex max-w-full items-center justify-center")}><CardLegend changeAlignment={changeAlignment} doughnutSeriesData={seriesData} toggleSeriesVisibility={toggleSeriesVisibility} onLegendItemHover={onLegendItemHover} onLegendItemLeave={onLegendItemLeave} isDeepLevel={isDeepLevel} /></div>;
  let t40;
  if ($[55] !== t31 || $[56] !== t37 || $[57] !== t38) {
    t40 = <div className={t31}>{t37}{t38}</div>;
    $[55] = t31;
    $[56] = t37;
    $[57] = t38;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  return t40;
}
function _temp2(data_1) {
  return {
    ...data_1,
    value: Math.abs(data_1.value)
  };
}
function _temp(data) {
  return data.color;
}
export { DoughnutChart };